import { useQuery } from '@apollo/client';
import React, { useEffect, useId, useMemo, useState } from 'react';

import { Checkbox, Input, Link, Select, SelectOption, Textarea } from '@flixbus/honeycomb-react';

import { FETCH_REASONS } from 'common/activeElements/modals/ModalAbsence/qql/fetchReasons';
import { FETCH_BREAKDOWN_STATIONS } from 'gql/breakdownStations';
import {
	BREAKDOWN_REASON_LABELS,
	BREAKDOWN_REASONS,
	REASONS_REQUIRING_ABSENCE,
} from 'utils/breakdown';

import { DEFAULT_ABSENCE_REASON_NAME } from './const';
import BreakdownStationAutocomplete from './inputs/BreakdownStationAutocomplete';
import GroupValidationMessage from './inputs/GroupValidationMessage';
import SegmentAutocomplete from './inputs/SegmentAutocomplete';
import { CREATE_BREAKDOWN_FLOW_TEXT } from './texts';
import { deriveBreakdownLocationOptions } from './utils/deriveBreakdownLocationOptions';
import { deriveSegmentOptions } from './utils/deriveSegmentOptions';
import { combineDateTime, getNowDate, getNowTime, splitTimestamp } from './utils/incidentTime';
import { isAtOrAfter } from './utils/timestampOrder';

import styles from './CommonInputsStep.module.css';

const COMMON_TEXT = CREATE_BREAKDOWN_FLOW_TEXT.common;
const ERR = CREATE_BREAKDOWN_FLOW_TEXT.errors;

const requiresAbsence = (reason) => REASONS_REQUIRING_ABSENCE.includes(reason);

/**
 * Step 1 — common inputs (v2, redesigned).
 *
 * Reads `data.assignment` (seeded via initialData) and renders:
 *   - date + time inputs (two separate fields; combined into
 *     `incidentTimestamp` in the draft as `YYYY-MM-DDTHH:mm`);
 *   - segment select (from consecutive station pairs of the assignment);
 *   - breakdown-location select (X-station, from-station, to-station);
 *   - reason select (bus failure / driver fatigue / driver out of hours);
 *   - conditional "Original driver absence" block for driver fatigue
 *     (date + time + optional absence reason);
 *   - "utilize parallel ride for passenger pickup" checkbox;
 *   - collapsible "Add a note" textarea.
 *
 * All fields emit through `onDraftChange` on every change; the shell reads
 * validity from `breakdownFlowConfig.nodes.common.validate`.
 *
 * @param {import('common/activeElements/modals/FlowModal/flowTypes').FlowNodeComponentProps} props
 */
const CommonInputsStep = ({ data, onDraftChange }) => {
	const reasonId = useId();
	const dateId = useId();
	const timeId = useId();
	const absenceDateId = useId();
	const absenceTimeId = useId();
	const absenceReasonId = useId();
	const parallelId = useId();
	const noteId = useId();

	const segmentOptions = useMemo(() => deriveSegmentOptions(data.assignment), [data.assignment]);

	const [initialDate, initialTime] = splitTimestamp(data.incidentTimestamp);
	const [absenceInitialDate, absenceInitialTime] = splitTimestamp(
		data.originalDriverAbsence?.fromTimestamp
	);

	const sequences = data.assignment?.workAssignmentSequences;
	const sequenceStartTimestamp = sequences?.[0]?.fromTimestamp;
	const sequenceStartTimezone = sequences?.[0]?.fromStation?.timezone;
	const [sequenceStartDate, sequenceStartTime] = splitTimestamp(
		sequenceStartTimestamp,
		sequenceStartTimezone
	);

	const [segmentIdValue, setSegmentIdValue] = useState(data.segmentId ?? '');
	const [breakdownStationId, setBreakdownStationId] = useState(data.breakdownStationId ?? '');
	const [breakdownReason, setBreakdownReason] = useState(data.breakdownReason ?? '');
	const [incidentDate, setIncidentDate] = useState(
		initialDate || sequenceStartDate || getNowDate()
	);
	const [incidentTime, setIncidentTime] = useState(
		initialTime || sequenceStartTime || getNowTime()
	);
	const [absenceDate, setAbsenceDate] = useState(absenceInitialDate);
	const [absenceTime, setAbsenceTime] = useState(absenceInitialTime);
	const [absenceReasonCode, setAbsenceReasonCode] = useState(
		data.originalDriverAbsence?.reasonCode ?? ''
	);
	const [parallelRideChosen, setParallelRideChosen] = useState(data.parallelRideChosen === true);
	const [noteOpen, setNoteOpen] = useState(Boolean(data.note));
	const [note, setNote] = useState(data.note ?? '');

	const selectedSegment = useMemo(
		() => segmentOptions.find((opt) => opt.id === segmentIdValue) ?? null,
		[segmentOptions, segmentIdValue]
	);

	const { data: breakdownStationsData } = useQuery(FETCH_BREAKDOWN_STATIONS);
	const { data: absenceReasonsData } = useQuery(FETCH_REASONS);
	const absenceReasonOptions = useMemo(
		() => absenceReasonsData?.crewMemberAbsenceReasons ?? [],
		[absenceReasonsData]
	);

	const defaultReasonCode = useMemo(
		() => absenceReasonOptions.find((opt) => opt.name === DEFAULT_ABSENCE_REASON_NAME)?.code ?? '',
		[absenceReasonOptions]
	);
	const effectiveReasonCode = absenceReasonCode || defaultReasonCode;

	const xStations = useMemo(
		() => breakdownStationsData?.breakdownStations ?? [],
		[breakdownStationsData]
	);

	const locationOptions = useMemo(
		() => deriveBreakdownLocationOptions(selectedSegment, xStations),
		[selectedSegment, xStations]
	);

	const selectedBreakdownStation = useMemo(
		() => locationOptions.find((opt) => opt.id === breakdownStationId) ?? null,
		[locationOptions, breakdownStationId]
	);

	const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
	const resolvedTimezone = selectedBreakdownStation?.station?.timezone ?? localTimezone;

	const absenceRequired = requiresAbsence(breakdownReason);
	const currentIncidentTimestamp = combineDateTime(incidentDate, incidentTime);
	const currentAbsenceTimestamp = combineDateTime(absenceDate, absenceTime);
	const absenceBeforeIncident = !isAtOrAfter(currentAbsenceTimestamp, currentIncidentTimestamp);
	const absenceGroupError = absenceBeforeIncident ? ERR.absenceBeforeIncident : undefined;

	useEffect(() => {
		const draft = {
			breakdownReason,
			incidentTimestamp: combineDateTime(incidentDate, incidentTime),
			incidentTimezone: resolvedTimezone || null,
			segmentId: segmentIdValue,
			fromStationId: selectedSegment?.fromStation?.id ?? '',
			toStationId: selectedSegment?.toStation?.id ?? '',
			breakdownStationId,
			parallelRideChosen,
		};
		if (selectedSegment) {
			draft.segment = selectedSegment;
		}
		if (selectedBreakdownStation?.station) {
			draft.breakdownStation = selectedBreakdownStation.station;
		}
		if (noteOpen && note) {
			draft.note = note;
		}
		if (absenceRequired) {
			const absenceTimestamp = combineDateTime(absenceDate, absenceTime);
			const originalDriverAbsence = {};
			if (absenceTimestamp) originalDriverAbsence.fromTimestamp = absenceTimestamp;
			if (effectiveReasonCode) originalDriverAbsence.reasonCode = effectiveReasonCode;
			if (Object.keys(originalDriverAbsence).length > 0) {
				draft.originalDriverAbsence = originalDriverAbsence;
			}
		}
		onDraftChange?.(draft);
	}, [
		breakdownReason,
		incidentDate,
		incidentTime,
		segmentIdValue,
		selectedSegment,
		selectedBreakdownStation,
		breakdownStationId,
		parallelRideChosen,
		noteOpen,
		note,
		absenceRequired,
		absenceDate,
		absenceTime,
		effectiveReasonCode,
		resolvedTimezone,
		onDraftChange,
	]);

	return (
		<div className={styles.stack}>
			<div className={styles.dateTimeRow}>
				<div className={styles.dateField}>
					<Input
						id={dateId}
						type="date"
						label={COMMON_TEXT.dateTimeLabel}
						min={sequenceStartDate}
						value={incidentDate}
						onChange={(event) => setIncidentDate(event.target.value)}
						data-testid="incident-date-input"
					/>
				</div>
				<div className={styles.timeField}>
					<Input
						id={timeId}
						type="time"
						aria-label={COMMON_TEXT.timeAriaLabel}
						value={incidentTime}
						onChange={(event) => setIncidentTime(event.target.value)}
						data-testid="incident-time-input"
					/>
				</div>
			</div>
			<p className={styles.timezoneNote}>
				<strong>{COMMON_TEXT.timezoneNoteLabel}</strong> {COMMON_TEXT.timezoneNote}
			</p>

			<div className={styles.locationRow}>
				<div className={styles.locationField}>
					<SegmentAutocomplete
						options={segmentOptions}
						value={segmentIdValue}
						onChange={(nextId) => {
							setSegmentIdValue(nextId);
							setBreakdownStationId('');
						}}
						label={COMMON_TEXT.segmentLabel}
						placeholder={COMMON_TEXT.segmentPlaceholder}
						testId="segment-select"
					/>
				</div>
				<div className={styles.locationField}>
					<BreakdownStationAutocomplete
						options={locationOptions}
						value={breakdownStationId}
						onChange={setBreakdownStationId}
						disabled={!selectedSegment}
						label={COMMON_TEXT.locationLabel}
						placeholder={COMMON_TEXT.locationPlaceholder}
						testId="breakdown-location-select"
					/>
				</div>
			</div>

			<Select
				id={reasonId}
				label={COMMON_TEXT.reasonLabel}
				value={breakdownReason}
				onChange={(event) => setBreakdownReason(event.target.value)}
				data-testid="breakdown-reason-select"
			>
				<SelectOption value="" disabled>
					{COMMON_TEXT.reasonPlaceholder}
				</SelectOption>
				{Object.values(BREAKDOWN_REASONS).map((r) => (
					<SelectOption key={r} value={r}>
						{BREAKDOWN_REASON_LABELS[r]}
					</SelectOption>
				))}
			</Select>

			{absenceRequired && (
				<div className={styles.absenceBox} data-testid="absence-box">
					<div className={styles.absenceGroup}>
						<div className={styles.dateTimeRow}>
							<div className={styles.dateField}>
								<Input
									id={absenceDateId}
									type="date"
									label={COMMON_TEXT.absenceStartLabel}
									value={absenceDate}
									onChange={(event) => setAbsenceDate(event.target.value)}
									valid={absenceBeforeIncident ? false : undefined}
									data-testid="absence-date-input"
								/>
							</div>
							<div className={styles.timeField}>
								<Input
									id={absenceTimeId}
									type="time"
									aria-label={COMMON_TEXT.absenceTimeAriaLabel}
									value={absenceTime}
									onChange={(event) => setAbsenceTime(event.target.value)}
									valid={absenceBeforeIncident ? false : undefined}
									data-testid="absence-time-input"
								/>
							</div>
						</div>
						<GroupValidationMessage message={absenceGroupError} testId="absence-group-error" />
					</div>
					<Select
						id={absenceReasonId}
						label={COMMON_TEXT.absenceReasonLabel}
						value={effectiveReasonCode}
						required
						onChange={(event) => setAbsenceReasonCode(event.target.value)}
						data-testid="absence-reason-select"
					>
						<SelectOption value="" disabled>
							{COMMON_TEXT.absenceReasonPlaceholder}
						</SelectOption>
						{absenceReasonOptions.map((opt) => (
							<SelectOption key={opt.id} value={opt.code}>
								{opt.isPayable ? `$ ${opt.name}` : opt.name}
							</SelectOption>
						))}
					</Select>
				</div>
			)}

			<div className={styles.checkbox}>
				<Checkbox
					id={parallelId}
					value="parallel-ride-chosen"
					label={COMMON_TEXT.parallelRideCheckboxLabel}
					checked={parallelRideChosen}
					onChange={(event) => setParallelRideChosen(event.target.checked)}
					data-testid="parallel-ride-checkbox"
				/>
			</div>

			{!noteOpen ? (
				<div className={styles.addNoteLink}>
					<Link
						href="#"
						onClick={(event) => {
							event.preventDefault();
							setNoteOpen(true);
						}}
						data-testid="add-note-toggle"
					>
						{COMMON_TEXT.addNoteLink}
					</Link>
				</div>
			) : (
				<div className={styles.noteField}>
					<Textarea
						id={noteId}
						label={COMMON_TEXT.noteLabel}
						value={note}
						onChange={(event) => setNote(event.target.value)}
						data-testid="note-input"
					/>
				</div>
			)}
		</div>
	);
};

export default CommonInputsStep;
